import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './VerifyEmail.module.css';

import { confirmInvitation, checkOnboardingStatus } from 'api/authApi';
import routes from 'routes';
import CustomInput from 'components/common/CustomInput/CustomInput';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import LabelText from 'components/common/LabelText';

const VerifyEmail = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const email = searchParams.get('email');
  const clubId = searchParams.get('clubId');

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [formValues, setFormValues] = React.useState({
    code: '',
  });

  const [errors, setErrors] = useState({});

  // Check onboarding status when component mounts
  useEffect(() => {
    const checkUserOnboardingStatus = async () => {
      if (!email) {
        setCheckingStatus(false);
        return;
      }

      try {
        const decodedEmail = atob(email);
        const response = await checkOnboardingStatus(decodedEmail);
        const { nextStep } = response.data;

        // If user has already completed email verification, redirect them
        switch (nextStep) {
          case 'set_password':
          case 'set_name_and_password':
            // User has confirmed email but needs to set password
            navigate(`${routes.auth.setPassword}?email=${email}`);
            return;
          case 'login':
            // User is fully onboarded, send them to login
            toast.info(
              'You have already completed verification. Please log in.'
            );
            navigate(routes.auth.login);
            return;
          case 'enter_invitation_code':
            // User still needs to enter invitation code - stay on this page
            break;
          case 'not_found':
            // User doesn't exist - this shouldn't happen with a valid invitation link
            // but we'll let them try to enter the code
            break;
          default:
            // Unknown status - let them try to enter the code
            break;
        }
      } catch (error) {
        // If API call fails, let them proceed with entering the code
        console.log('Error checking onboarding status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkUserOnboardingStatus();
  }, [email, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formValues.code) {
      newErrors.code = 'Verification code is required';
    }

    return newErrors;
  };

  const clickVerifyEmail = async () => {
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setLoading(true);
      try {
        const response = await confirmInvitation(
          clubId,
          atob(email),
          formValues.code
        );
        toast.success('Email verified successfully! Welcome to SplitFast.');
        if (response.data.needsPassword) {
          navigate(`${routes.auth.setPassword}?email=${email}`);
        } else {
          navigate(routes.auth.login);
        }
      } catch (e) {
        console.log('error: ', e);
        toast.error(
          e.response?.data?.title ||
            e.response?.data ||
            'Verification failed. Please check your code and try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  // Show loading state while checking onboarding status
  if (checkingStatus) {
    return (
      <div className={styles.verifyEmailContainer}>
        <div className={styles.headerSection}>
          <h1 className="heading1">Checking Status...</h1>
          <p className={styles.subtitle}>
            Please wait while we check your account status.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.verifyEmailContainer}>
        <div className={styles.headerSection}>
          <h1 className="heading1">Verify Your Email</h1>
          <p className={styles.subtitle}>
            Complete your registration by entering the verification code sent to
            your email.
          </p>
        </div>

        <div className={styles.emailSection}>
          <LabelText
            label="Email Address"
            text={email ? atob(email) : ''}
            style={{ marginBottom: 30 }}
          />
        </div>

        <div className={styles.inputSection}>
          <CustomInput
            name="code"
            placeholder="Enter verification code"
            style={{
              marginBottom: 20,
            }}
            inputStyle={{
              fontSize: '18px',
              height: '60px',
            }}
            onChange={handleInputChange}
            error={errors.code}
          />
        </div>

        <div className={styles.helpText}>
          <p>
            <strong>Need help?</strong> If you haven&apos;t received the code, check
            your spam folder. The code was sent when you clicked the invitation
            link in your email.
          </p>
        </div>

        <div className={styles.buttonSection}>
          <CustomButton
            disabled={loading}
            onClick={clickVerifyEmail}
            style={{
              ...defaultButtonStyle,
              width: '100%',
              maxWidth: 400,
            }}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
